
import { Options, Vue } from 'vue-class-component';
import { mapGetters } from 'vuex';
import Logo from '@/base-components/Logo.vue';
import store, { generateSid } from '@/store';
import { resolveGameUrl } from '@/utils/gameUrl';

const heroVideoContext = require.context('@/assets', false, /\.mp4$/);
const heroVideoMap: Record<string, string> = {};
heroVideoContext.keys().forEach((key: string) => {
  const match = key.match(/^\.\/(.+)\.mp4$/);
  if (match) heroVideoMap[match[1]] = heroVideoContext(key);
});

const heroInfoContext = require.context('@/assets', false, /-info\.jpg$/);
const heroInfoMap: Record<string, string> = {};
heroInfoContext.keys().forEach((key: string) => {
  const match = key.match(/^\.\/(.+)-info\.jpg$/);
  if (match) heroInfoMap[match[1]] = heroInfoContext(key);
});

@Options({
  props: {
    name: String
  },
  components: { Logo },
  computed: {
    ...mapGetters(['getJsonData', 'getGames', 'isAuthenticated', 'getPlayerSid', 'getVisibleInPopUp']),
    jsonData() {
      const jsonData = (this as any).getJsonData;
      const jsonDataWithBreaks = JSON.stringify(jsonData).replace(/\\n/g, '<br>');
      return JSON.parse(jsonDataWithBreaks);
    },
    routeName() {
      return (this as any).name || (this as any).$route.params.name;
    },
    gameData() {
      const games = (this as any).getGames;
      return games.find((g: {name: string}) => g.name === (this as any).routeName) || null;
    },
    heroImage() {
      const game = (this as any).gameData;
      if (game && game.heroImage) return game.heroImage;
      return require('@/assets/' + ((game && game.image) || (this as any).routeName) + '.jpg');
    },
    heroVideoSrc() {
      const game = (this as any).gameData;
      if (!game) return null;
      const key = game.heroVideo || game.name;
      return heroVideoMap[key] || null;
    },
    heroInfoImageSrc() {
      const game = (this as any).gameData;
      if (!game) return null;
      const key = game.image || game.name;
      return heroInfoMap[key] || null;
    },
    isInDev() {
      const game = (this as any).gameData;
      return !!game && (game.inDev === true || game.inDev === 'true');
    },
    description() {
      const key = (this as any).gameData && (this as any).gameData.description;
      return key ? (this as any).jsonData[key] : '';
    },
    specRows() {
      const spec = (this as any).gameData && (this as any).gameData.spec;
      if (!spec) return [];
      const jsonData = (this as any).jsonData;
      const rows = [
        { label: jsonData.gameSpecReleaseDate, value: spec.releaseDate },
        { label: jsonData.gameSpecRtp, value: spec.rtpPercent },
        { label: jsonData.gameSpecVolatility, value: spec.volatility },
        { label: jsonData.gameSpecHitFrequency, value: spec.hitFrequency },
        { label: jsonData.gameSpecMaxPayout, value: spec.maxPayout },
        { label: jsonData.gameSpecReelsRows, value: spec.reelsRows },
        { label: jsonData.gameSpecFeatureBuyIn, value: spec.featureBuyIn },
        { label: jsonData.gameSpecBonusMode, value: spec.bonusMode },
        { label: jsonData.gameSpecMinMaxBet, value: `${spec.minBet} / ${spec.maxBet}` },
        { label: jsonData.gameSpecPlayLinesMax, value: spec.playLinesMax },
      ];
      return rows.filter(row => row.value);
    }
  },
  watch: {
    getVisibleInPopUp(visible: boolean) {
      const video = (this as any).$refs.heroVideo as HTMLVideoElement | undefined;
      if (!video) return;
      if (visible) {
        video.pause();
      } else {
        video.play().catch(() => undefined);
      }
    }
  },
  methods: {
    openIframe(sid: string) {
      const game = (this as any).gameData;
      const sessionUrl = game.url + (game.url.includes('?') ? '&' : '?') + 'sid=' + encodeURIComponent(sid);
      store.commit('setUrlInPopUp', { game: game.game, url: resolveGameUrl(sessionUrl, game.name), visible: true });
    },
    playDemo() {
      const game = (this as any).gameData;
      const sessionUrl = game.url + (game.url.includes('?') ? '&' : '?') + 'sid=' + encodeURIComponent(generateSid());
      window.open(resolveGameUrl(sessionUrl, game.name), '_blank', 'noopener,noreferrer');
    },
    playInCoins() {
      if ((this as any).isAuthenticated) {
        (this as any).openIframe((this as any).getPlayerSid);
      } else {
        store.commit('SET_AUTH_MODAL', { visible: true, mode: 'login' });
      }
    }
  }
})
export default class GameDetailView extends Vue {
  name!: string
}
